/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {type APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import type {NewAgent} from '../../../types/agent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';
import {mapAgentLocalToRemote} from '../utils';

export const useUpdateAgent = () => {
  const agentsAPI = useAgentsAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agent, setAgent] = useState<NewAgent | null>(null);

  const update = useCallback(
    async (localAgent: NewAgent) => {
      setIsLoading(true);
      setError(null);

      try {
        const mappedAgent = mapAgentLocalToRemote(localAgent);
        const result = await agentsAPI({
          eventName: AGENTS_API_EVENT_NAMES.ManageAgent,
          methodName: METHODS.POST,
          params: {agent: mappedAgent},
          queryParams: {},
        } as APIParams);

        setAgent(result.agent);
        setIsLoading(false);
        return result.agent;
      } catch (err) {
        setIsLoading(false);
        setError('Failed to update agent');
        return Promise.reject(err);
      }
    },
    [agentsAPI],
  );

  return {
    agent,
    isLoading,
    error,
    update,
  };
};
